import ecoPlatformLogo from 'assets/images/eco-epd-logo.png';
import fallbackImage from 'assets/images/image-placeholder.svg';
import { EPDProductClassificationId } from 'components/v2/compiler/constants';
import epdInternationalLogo from 'images/EPD-logo.svg';
import React from 'react';
import FileService from 'services/FileService';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { CustomLogotypeId } from 'types/CustomLogotypeId';
import { ProductStatusEnum } from 'types/types';
import { getSelectedConformityStandards } from 'util/conformityStandards';
import DebugOnly from 'util/dev-tools/DebugOnly';
import RenderCounter from 'util/dev-tools/RenderCounter';
import { dateToIsoString, getOptionsFromDictionary } from 'util/utils';

import ImageWithFallback from 'components/error-fallback/ImageWithFallback';
import usePcrV2 from 'components/v2/compiler/hooks/usePcrV2';
import {
  BottomText,
  Container,
  DeclarationAccordance,
  DeclarationHeader,
  DeclarationName,
  GreenLeftLine,
  LeftSubsection,
  LeftSubsectionInnerContainer,
  PdfCoverPage,
  ProductsIncluded,
  ProductsIncludedHeader,
  RightSubsection,
  RightSubsectionCaption,
  RightSubsectionText,
  SecondSection,
  ThirdSection,
  TopSection,
} from './Styles.styled';
import usePdfData from './utils/usePdfData';

const CoverPage: React.FC<{
  qrImageUrl: string | null;
}> = ({ qrImageUrl }) => {
  const isPcrV2 = usePcrV2();
  const { coverPageInformation, epdDefinition, products, organizationInformation, dictionaryValues } = usePdfData();

  const epdClassificationOptions = getOptionsFromDictionary(
    dictionaryValues,
    isPcrV2 ? EPD_DICTIONARY_CODES.EPD_CLASSIFICATION_V2 : EPD_DICTIONARY_CODES.EPD_CLASSIFICATION
  );
  const epdClassification = epdClassificationOptions.find((x: any) => x.value == epdDefinition.epdClassification);
  const productsInEPD =
    products.length <= 10
      ? products?.map((x) => x.productName).join(', ')
      : 'The EPD covers more than 10 products. Please refer to the product information section for the list of products.';
  const allProductsOnTheMarket = products.every(
    (p) => p.productStatus === ProductStatusEnum.OnTheMarket || !p.productStatus
  );
  const showDisclaimerStatusForMultipleProducts =
    isPcrV2 && !allProductsOnTheMarket && epdDefinition.epdClassification != EPDProductClassificationId.SINGLE_PRODUCT_ID;
  const showDisclaimerStatusForSingleProduct =
    isPcrV2 && !allProductsOnTheMarket && epdDefinition.epdClassification == EPDProductClassificationId.SINGLE_PRODUCT_ID;
  const inAccordanceWithStandards = getSelectedConformityStandards(
    epdDefinition?.statementOfConformityWithStandards,
    dictionaryValues
  )
    .map((x) => x.label)
    .join(', ');

  const licensee = organizationInformation?.licenseeName;
  const collocationCenter = organizationInformation?.collocationCenterName;
  const companyLogotypeImage = FileService.getImageUrl(coverPageInformation?.companyLogoImages![0]?.id);

  const getLogotypeSrc = () => {
    if (!licensee && !collocationCenter) {
      return epdInternationalLogo;
    }

    // licensee || collocationCenter
    switch (epdDefinition.customLogotype) {
      case CustomLogotypeId.COLLOCATION_CENTER_LOGOTYPE_ID:
        return collocationCenter ? organizationInformation.collocationCenterLogotypeUrl : fallbackImage;
      case CustomLogotypeId.LICENSEE_LOGOTYPE_ID:
        return licensee ? organizationInformation.licenseeLogotypeUrl : fallbackImage;
      case CustomLogotypeId.IES_LOGOTYPE_ID:
        return epdInternationalLogo;
      default:
        return fallbackImage;
    }
  };

  return (
    <PdfCoverPage className="cover">
      <DebugOnly>
        <RenderCounter componentName="cover page" threshold={3} />
      </DebugOnly>
      <GreenLeftLine />
      <Container>
        <TopSection>
          <LeftSubsection>
            <LeftSubsectionInnerContainer>
              <div>
                <div style={{ padding: '1cm 0 1.5cm 0' }}>
                  <DeclarationHeader>
                    Environmental <br />
                    product <br />
                    declaration
                  </DeclarationHeader>
                </div>
                <div style={{ paddingBottom: '0.5cm' }}>
                  <DeclarationName>{epdDefinition.declarationName}</DeclarationName>
                  <DeclarationAccordance>In accordance with: {inAccordanceWithStandards}</DeclarationAccordance>
                </div>
                <div>
                  <ProductsIncludedHeader>Products included in the EPD:</ProductsIncludedHeader>
                  <ProductsIncluded>{productsInEPD}</ProductsIncluded>
                  {showDisclaimerStatusForSingleProduct &&
                    (products[0].productStatus === ProductStatusEnum.ForthcomingOrNotYetOnTheMarket ? (
                      <ProductsIncluded>
                        Product not yet on the market - Results of this EPD shall be used with care as the LCI data is not
                        yet based on 1 year of production which may result in increased uncertainty.
                      </ProductsIncluded>
                    ) : (
                      <ProductsIncluded>
                        Product recently on the market - Results of this EPD shall be used with care as the LCI data is not
                        yet based on 1 year of production which may result in increased uncertainty.
                      </ProductsIncluded>
                    ))}
                  {showDisclaimerStatusForMultipleProducts && (
                    <ProductsIncluded>
                      One or more products are not yet or recently on the market. Please refer to the product information
                      section for the market status of each product - Results of this EPD shall be used with care as the LCI
                      data is not yet based on 1 year of production which may result in increased uncertainty.
                    </ProductsIncluded>
                  )}
                </div>
              </div>

              <div>
                {products.some((x) => x.isTheProjectInThePlanningOrDesignPhase === true) && (
                  <BottomText>Project in planning/design phase</BottomText>
                )}
                <BottomText>
                  An EPD may be updated or depublished if conditions change. To find the latest version of the EPD and to
                  confirm its validity, see www.environdec.com
                </BottomText>
                {!!epdClassification && <BottomText>{epdClassification.label}</BottomText>}
              </div>
            </LeftSubsectionInnerContainer>
          </LeftSubsection>
          <RightSubsection>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
              <div>
                <RightSubsectionCaption>EPD Owner</RightSubsectionCaption>
                <RightSubsectionText>{organizationInformation?.companyName}</RightSubsectionText>

                <RightSubsectionCaption>Programme</RightSubsectionCaption>
                <RightSubsectionText>
                  International EPD System
                  <br />
                  www.environdec.com
                </RightSubsectionText>

                <RightSubsectionCaption>Programme operator</RightSubsectionCaption>
                <RightSubsectionText>EPD International AB</RightSubsectionText>
                {licensee && (
                  <>
                    <RightSubsectionCaption>Licensee</RightSubsectionCaption>
                    <RightSubsectionText>{licensee}</RightSubsectionText>
                  </>
                )}

                <RightSubsectionCaption>Registration number</RightSubsectionCaption>
                <RightSubsectionText>{epdDefinition.fullIdentificationNumber}</RightSubsectionText>

                <RightSubsectionCaption>Version date</RightSubsectionCaption>
                <RightSubsectionText>{dateToIsoString(epdDefinition?.versionDate)}</RightSubsectionText>

                <RightSubsectionCaption>Validity date</RightSubsectionCaption>
                <RightSubsectionText>{dateToIsoString(epdDefinition?.validityDate)}</RightSubsectionText>
              </div>
              {qrImageUrl && (
                <div style={{ display: 'flex', alignContent: 'flex-end', justifyContent: 'flex-end' }}>
                  <img style={{ maxWidth: '75px', aspectRatio: 0.75 }} src={qrImageUrl} alt="QR code" />
                </div>
              )}
            </div>
          </RightSubsection>
        </TopSection>
        <SecondSection $hasImage={!!coverPageInformation?.coverPageProductImage?.id}>
          <img
            height={'100%'}
            width={'100%'}
            src={FileService.getImageUrl(coverPageInformation?.coverPageProductImage?.id)}
            alt="Cover page image"
          />
        </SecondSection>
        <ThirdSection>
          <div>
            <ImageWithFallback
              src={getLogotypeSrc()}
              fallback={fallbackImage}
              alt="EPD Logotype"
              height={40}
              style={{ maxHeight: '40px' }}
            />
          </div>
          <div>
            <img height={40} style={{ maxHeight: '40px' }} src={ecoPlatformLogo} alt="ECO Platform logotype" />
          </div>
          <div>
            <ImageWithFallback
              src={companyLogotypeImage}
              fallback={fallbackImage}
              alt="Organization logotype"
              height={40}
              style={{ maxHeight: '40px' }}
            />
          </div>
        </ThirdSection>
      </Container>
    </PdfCoverPage>
  );
};

export default CoverPage;
